import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Animation, Button, ButtonsContainer, Flex, TabFooter, Tabs, TabsContainer } from '../../../styling/GlobalStyles'
import { CancelBtn } from 'components/Button/CancelBtn'
import { ToggleButton } from 'components/Input/ToggleButton'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { OtherText } from 'styling/Typography'
import { INotificationRolePrefs } from 'types/notificationCenter.interface'
import { AppDispatch } from 'store'
import { updateRoleNotificationSettings } from 'features/roles/clientRolesSlice'
import {
  ALWAYS_ON_NOTIFICATIONS,
  CATEGORY_HINTS,
  MANDATORY_NOTIFICATION_CATEGORIES,
  MODULE_CATEGORIES,
  NOTIFICATION_MODULES,
  rolePrefsFromApi,
  rolePrefsToApi,
  typesInCategory,
} from 'pages/NotificationCenter/notificationRegistry'
import {
  SectionTitle,
  CategoryInfo,
  CategoryName,
  CategoryHint,
  AlwaysOnList,
  AlwaysOnChip,
  HelperNote,
  CategoryBlock,
  CategoryHead,
  HeadChevron,
  TypeList,
  TypeRow,
  TypeName,
  LockTag,
  PendingTag,
  OffNote,
} from './NotificationsRolesTab.styled'

export const NotificationsRolesTab = ({ state }: any) => {
  const dispatch = useDispatch<AppDispatch>()
  const [prefs, setPrefs] = useState<INotificationRolePrefs>(() =>
    rolePrefsFromApi(state.notificationSettings),
  )
  const [openCats, setOpenCats] = useState<{ [category: string]: boolean }>({})
  const [activeModule, setActiveModule] = useState('hr')

  useEffect(() => {
    setPrefs(rolePrefsFromApi(state.notificationSettings))
  }, [state.id, state.notificationSettings])

  const toggleCategory = (category: string) =>
    setPrefs((prev) => ({
      ...prev,
      categories: { ...prev.categories, [category]: !prev.categories[category] },
    }))

  const toggleType = (type: string) =>
    setPrefs((prev) => ({
      ...prev,
      types: { ...prev.types, [type]: !(prev.types[type] !== false) },
    }))

  const toggleOpen = (category: string) =>
    setOpenCats((prev) => ({ ...prev, [category]: !prev[category] }))

  const save = async () => {
    if (!state.id) return
    await dispatch(
      updateRoleNotificationSettings({ roleId: state.id, body: rolePrefsToApi(prefs) }),
    )
  }

  const moduleCategories = MODULE_CATEGORIES[activeModule] || []
  const mandatory = moduleCategories.filter((c) => MANDATORY_NOTIFICATION_CATEGORIES.includes(c))
  const optional = moduleCategories.filter((c) => !MANDATORY_NOTIFICATION_CATEGORIES.includes(c))
  const moduleName = NOTIFICATION_MODULES.find((m) => m.key === activeModule)?.name || ''

  return (
    <Animation>
      <Flex width={screen.availWidth < 500 ? '100%' : '60%'} column gap='0.5rem'>
        <OtherText grey>
          Everyone with this role receives the categories and notifications enabled below. Changes
          apply to the bell and the notification inbox.
        </OtherText>

        <SectionTitle>Always delivered — to every user, cannot be disabled</SectionTitle>
        <AlwaysOnList>
          {ALWAYS_ON_NOTIFICATIONS.map((n) => (
            <AlwaysOnChip key={n.type} title={n.pending || undefined}>
              🔒 {n.label}
              {n.pending ? ' · soon' : ''}
            </AlwaysOnChip>
          ))}
        </AlwaysOnList>
        <HelperNote>
          Personal notifications (decisions on your own requests, your shift confirmations) and
          safety-critical alerts reach every user regardless of role settings.
        </HelperNote>

        <SectionTitle>Module</SectionTitle>
        <TabsContainer>
          {NOTIFICATION_MODULES.map((m) => (
            <Tabs key={m.key} active={activeModule === m.key} onClick={() => setActiveModule(m.key)}>
              {m.name}
            </Tabs>
          ))}
        </TabsContainer>

        {mandatory.length > 0 && <SectionTitle>Locked categories — {moduleName}</SectionTitle>}
        {mandatory.map((c) => {
          const open = !!openCats[c]
          return (
            <CategoryBlock key={c}>
              <CategoryHead $clickable onClick={() => toggleOpen(c)}>
                <HeadChevron $open={open}>›</HeadChevron>
                <CategoryInfo>
                  <CategoryName>{c}</CategoryName>
                  <CategoryHint>{CATEGORY_HINTS[c]}</CategoryHint>
                </CategoryInfo>
                <Flex style={{ marginLeft: 'auto' }}>
                  <StatusPill status='accent'>Always on</StatusPill>
                </Flex>
              </CategoryHead>
              {open && (
                <TypeList>
                  {typesInCategory(c).map(({ type, def }) => (
                    <TypeRow key={type}>
                      <TypeName>{def.label}</TypeName>
                      <Flex center gap='0.5rem'>
                        {def.pending && <PendingTag title={def.pending}>not yet live</PendingTag>}
                        {def.alwaysOn ? <LockTag>🔒 Always on</LockTag> : <LockTag>Included</LockTag>}
                      </Flex>
                    </TypeRow>
                  ))}
                </TypeList>
              )}
            </CategoryBlock>
          )
        })}

        <SectionTitle>Optional categories — {moduleName}</SectionTitle>
        {optional.map((c) => {
          const enabled = prefs.categories[c] !== false
          const open = !!openCats[c] && enabled
          return (
            <CategoryBlock key={c} $dim={!enabled}>
              <CategoryHead $clickable={enabled} onClick={() => enabled && toggleOpen(c)}>
                <HeadChevron $open={open}>›</HeadChevron>
                <CategoryInfo>
                  <CategoryName>{c}</CategoryName>
                  <CategoryHint>{CATEGORY_HINTS[c]}</CategoryHint>
                </CategoryInfo>
                <Flex style={{ marginLeft: 'auto' }} onClick={(e: any) => e.stopPropagation()}>
                  <ToggleButton state={enabled} handleChange={() => toggleCategory(c)} name={c} />
                </Flex>
              </CategoryHead>
              {open && (
                <TypeList>
                  {typesInCategory(c).map(({ type, def }) =>
                    def.alwaysOn ? (
                      <TypeRow key={type}>
                        <TypeName>{def.label}</TypeName>
                        <Flex center gap='0.5rem'>
                          {def.pending && <PendingTag title={def.pending}>not yet live</PendingTag>}
                          <LockTag>🔒 Always on</LockTag>
                        </Flex>
                      </TypeRow>
                    ) : (
                      <TypeRow key={type}>
                        <TypeName>{def.label}</TypeName>
                        <Flex center gap='0.5rem'>
                          {def.pending && <PendingTag title={def.pending}>not yet live</PendingTag>}
                          <ToggleButton
                            state={prefs.types[type] !== false}
                            handleChange={() => toggleType(type)}
                            name={type}
                          />
                        </Flex>
                      </TypeRow>
                    ),
                  )}
                </TypeList>
              )}
              {!enabled && <OffNote>Category off — only always-on notifications are delivered.</OffNote>}
            </CategoryBlock>
          )
        })}
      </Flex>
      <TabFooter>
        <ButtonsContainer>
          <CancelBtn />
          <Button orange type='button' onClick={save}>
            Save
          </Button>
        </ButtonsContainer>
      </TabFooter>
    </Animation>
  )
}
